import { Injectable, NgZone, PlatformRef } from '@angular/core';
import { ProcessorService } from '@df/ng/processor.service';

const globalObject = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : undefined;

const nativeSetTimeout = (globalObject && globalObject['__zone_symbol__setTimeout']) || setTimeout;
const nativeSetInterval = (globalObject && globalObject['__zone_symbol__setInterval']) || setInterval;

export enum EAsyncPriority {
    // immediately, but non-blocking
    immeditate,
    // on processor ready
    bootstrap,
    // when app is idle
    idle,
    // later
    low
}
@Injectable({
    providedIn: 'root'
})
export class TimeoutService {
    constructor(
        readonly ngZone: NgZone,
        private platformRef: PlatformRef
    ) {}

    platformSafe(cb: () => void | any, inZone = true) {
        if (!this.platformRef.destroyed && !(ProcessorService.injector as any)?.destroyed) {
            inZone ? this.ngZone.run(cb) : this.ngZone.runOutsideAngular(cb);
        }
    }
    /**
     * Perform opetation asynchronously
     */
    async(cb, priority: EAsyncPriority = EAsyncPriority.immeditate, options?: { timeout?: number }) {
        switch (priority) {
            case EAsyncPriority.bootstrap:
                return this.async(() => ProcessorService.onReady(() => this.async(() => cb())));
            case EAsyncPriority.immeditate:
                return this.setTimeout(cb);
            case EAsyncPriority.low:
                return this.async(cb, EAsyncPriority.idle, { timeout: 8000 });
            case EAsyncPriority.idle:
                if (typeof window !== 'undefined' && window['requestIdleCallback']) {
                    window['requestIdleCallback'](() => this.platformSafe(cb), options?.timeout ? { timeout: options.timeout } : undefined);
                } else {
                    this.setTimeout(cb, options?.timeout || 5000);
                }
        }
    }

    /**
     * Sets timeout outside angular zone
     */
    setTimeout(cb: () => void, delay?: number, inZone = true): number {
        // return this.ngZone.runOutsideAngular(() => {
        return <number>(<any>nativeSetTimeout(
            () => this.platformSafe(cb, inZone),

            delay
        ));
        // });
    }

    setInterval(cb: () => void, delay?: number): number {
        // return this.ngZone.runOutsideAngular(() => {
        const intervalId = <number>(<any>nativeSetInterval(() => {
            if (!this.platformRef.destroyed) {
                cb();
                // this.ngZone.run(cb); // not needed as we dont run outside ng zone, we just use native timeout
            } else {
                clearInterval(intervalId);
            }
        }, delay));

        // });
        return intervalId;
    }

    /**
     * Returns debounced method
     */
    debounce<T extends (...args: any) => any>(func: T, wait: number = 0, immediate?: boolean): T {
        const that = this;
        let timeout: number | undefined;
        let previous = 0;
        let args: any[] | undefined;
        let result: T | undefined;
        let context: any;

        const later = function () {
            const passed = new Date().getTime() - previous;
            if (wait > passed) {
                timeout = that.setTimeout(later, wait - passed);
            } else {
                timeout = undefined;
                if (!immediate) result = func.apply(context, args || []);
                // This check is needed because `func` can recursively invoke `debounced`.
                if (!timeout) args = context = undefined;
            }
        };

        const debounced = <any>that.restArguments(function (this: any, _args: any) {
            context = this;
            args = _args;
            previous = new Date().getTime();
            if (!timeout) {
                timeout = that.setTimeout(later, wait);
                if (immediate) result = func.apply(context, args || []);
            }
            return result;
        });

        debounced.cancel = function () {
            clearTimeout(timeout);
            timeout = args = context = undefined;
        };

        return debounced;
    }

    /**
     * Exact copy of underscor throttle method
     * changed setTimeout to use ngZone protected version
     */
    throttle<T extends (...args: any) => any>(func: T, wait: number = 0, options: { leading?: boolean; trailing?: boolean } = {}) {
        const that = this;

        let timeout: number | undefined;
        let context: any;
        let args;
        let result;
        let previous = 0;

        const later = function () {
            previous = options.leading === false ? 0 : new Date().getTime();
            timeout = undefined;
            result = func.apply(context, args);
            if (!timeout) context = args = null;
        };

        const throttled = function (this: any) {
            const _now = new Date().getTime();
            if (!previous && options.leading === false) previous = _now;
            const remaining = wait - (_now - previous);
            context = this;
            args = arguments;
            if (remaining <= 0 || remaining > wait) {
                if (timeout) {
                    clearTimeout(timeout);
                    timeout = undefined;
                }
                previous = _now;
                result = func.apply(context, args);
                if (!timeout) {
                    context = args = undefined;
                }
            } else if (!timeout && options.trailing !== false) {
                timeout = that.setTimeout(later, remaining);
            }
            return result;
        };

        throttled.cancel = function () {
            clearTimeout(timeout);
            previous = 0;
            timeout = context = args = undefined;
        };

        return throttled;
    }

    private restArguments(func, _startIndex?: number) {
        const startIndex = typeof _startIndex === 'number' ? _startIndex : func.length - 1;
        return function (this: any) {
            const length = Math.max(arguments.length - startIndex, 0);
            const rest = Array(length);
            let index = 0;
            for (; index < length; index++) {
                rest[index] = arguments[index + startIndex];
            }
            switch (startIndex) {
                case 0:
                    return func.call(this, rest);
                case 1:
                    return func.call(this, arguments[0], rest);
                case 2:
                    return func.call(this, arguments[0], arguments[1], rest);
            }
            const args = Array(startIndex + 1);
            for (index = 0; index < startIndex; index++) {
                args[index] = arguments[index];
            }
            args[startIndex] = rest;
            return func.apply(this, args);
        };
    }
}
